import React, { useEffect, useRef, useState } from 'react';
import { Plus, Minus, MapPin } from 'lucide-react';
export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Prototype map. Drag to move around, click to drop the pin, +/- to zoom.
 * In the build this is a real map (Google Maps or Mapbox) with the same behaviour.
 */
export function WalletMapPicker({
  centre,
  onCentreChange,
  pin,
  onPinChange,
  height = 320
}: {
  centre: LatLng;
  onCentreChange: (c: LatLng) => void;
  pin: LatLng | null;
  onPinChange: (p: LatLng) => void;
  height?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [zoom, setZoom] = useState(3);
  const drag = useRef<{
    x: number;
    y: number;
    moved: boolean;
    start: LatLng;
  } | null>(null);
  useEffect(() => {
    if (ref.current) setWidth(ref.current.getBoundingClientRect().width);
  }, []);
  // degrees per pixel
  const scale = 0.00002 * Math.pow(2, 6 - zoom);
  const toLatLng = (clientX: number, clientY: number): LatLng => {
    const r = ref.current!.getBoundingClientRect();
    const dx = clientX - r.left - r.width / 2;
    const dy = clientY - r.top - r.height / 2;
    return {
      lat: +(centre.lat - dy * scale).toFixed(6),
      lng: +(centre.lng + dx * scale).toFixed(6)
    };
  };
  const pinPos = pin ? {
    x: width / 2 + (pin.lng - centre.lng) / scale,
    y: height / 2 - (pin.lat - centre.lat) / scale
  } : null;
  const bgX = -(centre.lng / scale) % 80;
  const bgY = centre.lat / scale % 80;
  return <div ref={ref} className="relative rounded-lg overflow-hidden border border-[#E2E8F0] select-none cursor-grab active:cursor-grabbing touch-none" style={{
    height,
    backgroundColor: '#E8F5E9',
    backgroundImage: 'linear-gradient(#C8E6C9 1px, transparent 1px), linear-gradient(90deg, #C8E6C9 1px, transparent 1px), linear-gradient(35deg, transparent 48%, #FFFFFF 48%, #FFFFFF 52%, transparent 52%), radial-gradient(circle at 30% 40%, #B3E5FC 0 14px, transparent 15px), radial-gradient(circle at 70% 70%, #C5E1A5 0 40px, transparent 41px)',
    backgroundSize: '80px 80px, 80px 80px, 320px 320px, 400px 400px, 520px 520px',
    backgroundPosition: `${bgX}px ${bgY}px, ${bgX}px ${bgY}px, ${bgX * 2}px ${bgY * 2}px, ${bgX * 3}px ${bgY * 3}px, ${bgX * 1.5}px ${bgY * 1.5}px`
  }} onPointerDown={e => {
    ref.current?.setPointerCapture?.(e.pointerId);
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      moved: false,
      start: centre
    };
  }} onPointerMove={e => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    if (d.moved) onCentreChange({
      lat: +(d.start.lat + dy * scale).toFixed(6),
      lng: +(d.start.lng - dx * scale).toFixed(6)
    });
  }} onPointerUp={e => {
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved) onPinChange(toLatLng(e.clientX, e.clientY));
  }} data-id="element-2562" data-mp="11v8fd9ev9xjb">
      {pinPos && <div className="absolute -translate-x-1/2 -translate-y-full pointer-events-none" style={{
      left: pinPos.x,
      top: pinPos.y
    }} data-id="element-2563" data-mp="32qx7r455h798">
          <MapPin className="w-8 h-8 text-[#DC2626] fill-[#DC2626] drop-shadow" data-id="element-2564" data-mp="37k041k1baut4" />
        </div>}
      <div className="absolute left-3 top-3 bg-white/90 rounded px-2 py-1 text-[11px] text-[#475569] shadow-sm pointer-events-none" data-id="element-2565" data-mp="2suu57x6dz6lr">
        Drag to move, click to drop the pin
      </div>
      <div className="absolute right-3 bottom-3 flex flex-col bg-white rounded-md shadow border border-[#E2E8F0]" onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()} data-id="element-2566" data-mp="3li807pqixs38">
        <button type="button" onClick={() => setZoom(z => Math.min(6, z + 1))} className="w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9]" aria-label="Zoom in" data-id="element-2567" data-mp="3biz5gunu49ft">
          <Plus className="w-4 h-4" data-id="element-2568" data-mp="203aja5ieneg2" />
        </button>
        <div className="h-px bg-[#E2E8F0]" data-id="element-2569" data-mp="206okvbj8vjmg" />
        <button type="button" onClick={() => setZoom(z => Math.max(0, z - 1))} className="w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9]" aria-label="Zoom out" data-id="element-2570" data-mp="254hr24kjsdmq">
          <Minus className="w-4 h-4" data-id="element-2571" data-mp="2gk4jlgquvdse" />
        </button>
      </div>
    </div>;
}